import { useEffect, useRef, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, ShoppingBag, ExternalLink, ShoppingCart, Check, Play, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { slugify, buildShareUrl, copyToClipboard, getAddressBarShareUrl } from "@/lib/share";

const VIDEO_EXTS = [".mp4", ".webm", ".mov", ".ogv", ".m4v"];
function isVideoUrl(url) {
  if (!url) return false;
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  return VIDEO_EXTS.some((ext) => clean.endsWith(ext));
}

// Renders an image OR an autoplaying muted video, depending on the URL.
// `mode`:
//   - "thumb"   — card thumbnail (autoplay muted loop)
//   - "lightbox"— full-size in modal (controls + autoplay muted)
function Media({ url, alt = "", mode = "thumb", className = "", style }) {
  if (!url) return null;
  if (isVideoUrl(url)) {
    return (
      <video
        src={url}
        autoPlay
        muted
        loop
        playsInline
        controls={mode === "lightbox"}
        className={className}
        style={style}
      />
    );
  }
  return <img src={url} alt={alt} className={className} style={style} />;
}

// Apply a percentage discount to every number found in a price string.
// e.g. applyDiscount("$45.00", 20) → "$36.00"
//      applyDiscount("$5.00 — $15.00", 20) → "$4.00 — $12.00"
function applyDiscount(priceStr, pct) {
  if (!priceStr || !pct || pct <= 0 || pct >= 100) return null;
  let touched = false;
  const out = priceStr.replace(/(\d+(?:\.\d+)?)/g, (m) => {
    touched = true;
    const n = parseFloat(m);
    const v = n * (1 - pct / 100);
    // Preserve 2 decimal places when original had decimals; else keep integer-ish
    return m.includes(".") ? v.toFixed(2) : String(Math.round(v));
  });
  return touched ? out : null;
}

const ComingSoonLED = ({ name }) => (
  <div
    data-testid="led-coming-soon"
    className="relative border border-[var(--theme-border)] bg-black rounded-2xl overflow-hidden py-16 md:py-24 px-6 text-center"
    style={{
      backgroundImage:
        "radial-gradient(circle, rgba(var(--theme-accent-primary-rgb),0.10) 1px, transparent 1.5px)",
      backgroundSize: "14px 14px",
    }}
  >
    {/* glow halo */}
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(var(--theme-accent-primary-rgb),0.14), transparent 65%)",
      }}
    />
    {/* LED label */}
    <div className="relative z-10 flex flex-col items-center gap-3">
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.4em] text-[var(--accent)]">
        <span className="w-2 h-2 bg-[var(--accent)] rounded-full animate-pulse" />
        SIGN_ACTIVE
        <span className="w-2 h-2 bg-[var(--accent)] rounded-full animate-pulse" />
      </div>
      <h3
        className="led-text led-flicker font-display text-5xl md:text-7xl lg:text-8xl font-extrabold tracking-[0.15em]"
      >
        COMING SOON
      </h3>
      <div className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] uppercase mt-2">
        // {name} // stock incoming
      </div>
    </div>
    {/* corner ticks */}
    <div className="absolute top-2 left-2 w-3 h-3 border-l border-t border-[var(--accent)]" />
    <div className="absolute top-2 right-2 w-3 h-3 border-r border-t border-[var(--accent)]" />
    <div className="absolute bottom-2 left-2 w-3 h-3 border-l border-b border-[var(--accent)]" />
    <div className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-[var(--accent)]" />
  </div>
);

export default function ProductsSection({ categories = [] }) {
  const sortedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
  const [activeId, setActiveId] = useState(sortedCats[0]?.id || null);
  const [openProduct, setOpenProduct] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    if (!sortedCats.find((c) => c.id === activeId) && sortedCats[0]) {
      setActiveId(sortedCats[0].id);
    }
  }, [sortedCats, activeId]);

  useEffect(() => {
    if (openProduct) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [openProduct]);

  useEffect(() => { setLightboxIdx(null); }, [openProduct]);

  // Sync URL <-> open product. When a product is opened, mirror the URL bar to
  // the SHARE URL (`/api/share/<slug>`) so whatever the user copies from the
  // address bar previews correctly on Discord/Twitter. We also push a real
  // history entry so the browser BACK button works (and the X button closes
  // by simply popping that entry).
  const hadOpenProduct = useRef(false);
  useEffect(() => {
    if (openProduct) {
      const slug = slugify(openProduct.title || openProduct.name || openProduct.id);
      if (!slug) return;
      const target = getAddressBarShareUrl(slug, "product");
      if (window.location.pathname + window.location.search === target) {
        hadOpenProduct.current = true;
        return;
      }
      if (hadOpenProduct.current) {
        window.history.replaceState({ nmModal: slug }, "", target);
      } else {
        window.history.pushState({ nmModal: slug }, "", target);
        hadOpenProduct.current = true;
      }
    } else if (hadOpenProduct.current) {
      hadOpenProduct.current = false;
      if (window.location.pathname.startsWith("/api/share/")) {
        window.history.back();
      }
    }
  }, [openProduct]);

  // Listen for browser back/forward — close the modal cleanly when the URL is
  // no longer a share URL. (We don't re-open a different modal on back here;
  // PortfolioSection handles its own slugs the same way.)
  useEffect(() => {
    const handler = () => {
      if (!window.location.pathname.startsWith("/api/share/")) {
        hadOpenProduct.current = false;
        setOpenProduct(null);
      }
    };
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  // On mount: auto-open the matching product from ?v=<slug>. Wait until
  // categories are populated; one-shot guard via ref. Honor the URL hash —
  // `#portfolio` means the user wants the gallery archive, skip here so
  // PortfolioSection takes over.
  const deeplinkConsumed = useRef(false);
  useEffect(() => {
    if (deeplinkConsumed.current) return;
    if (!sortedCats?.length) return;
    const params = new URLSearchParams(window.location.search);
    const slug = params.get("v");
    if (!slug) {
      deeplinkConsumed.current = true;
      return;
    }
    // Respect explicit hash routing — `#portfolio` is reserved for the
    // PortfolioSection's auto-opener.
    if (window.location.hash === "#portfolio") {
      // Don't claim this slug; let PortfolioSection handle it.
      return;
    }
    for (const cat of sortedCats) {
      const match = (cat.products || []).find(
        (p) => slugify(p.title || p.name || p.id) === slug
      );
      if (match) {
        deeplinkConsumed.current = true;
        // Synchronously signal to PortfolioSection that this slug is taken.
        window.__nullDeepLinkClaimed = true;
        // Set up history so the browser back button takes the visitor to "/"
        // (the landing page) instead of out of the site. We rewrite the
        // current `/?v=<slug>#products` entry to "/" then push the share URL
        // on top of it. Pressing back now pops back to "/".
        try {
          const target = getAddressBarShareUrl(slug, "product");
          // Preserve the current section page as the previous history entry
          // so back-button lands on the shop listing (or homepage), not away.
          const backTarget = window.location.pathname + window.location.search;
          window.history.replaceState({}, "", backTarget.startsWith("/api/share/") ? "/products" : backTarget.split("?")[0]);
          window.history.pushState({ nmModal: slug }, "", target);
          hadOpenProduct.current = true; // we already pushed; URL-sync effect should skip
        } catch (_) { /* ignore older browsers without history API */ }
        setActiveId(cat.id);
        setOpenProduct(match);
        setTimeout(() => {
          document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
        return;
      }
    }
    // No product match — leave the field open for PortfolioSection.
  }, [sortedCats]);

  const handleCopyShareLink = useCallback(async () => {
    if (!openProduct) return;
    const slug = slugify(openProduct.title || openProduct.name || openProduct.id);
    const url = buildShareUrl(slug, "product");
    const ok = await copyToClipboard(url);
    if (ok) {
      toast.success("Share link copied", {
        description: "Pasting this in Discord/Twitter previews with the product's thumbnail.",
      });
    } else {
      toast.error("Couldn't copy — try selecting the URL manually");
    }
  }, [openProduct]);

  const handleKey = useCallback((e) => {
    if (!openProduct) return;
    if (e.key === "Escape") {
      if (lightboxIdx !== null) setLightboxIdx(null);
      else setOpenProduct(null);
      return;
    }
    if (lightboxIdx !== null && openProduct.images?.length) {
      if (e.key === "ArrowRight") setLightboxIdx((i) => (i + 1) % openProduct.images.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((i) => (i - 1 + openProduct.images.length) % openProduct.images.length);
    }
  }, [openProduct, lightboxIdx]);

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!sortedCats.length) return null;

  const active = sortedCats.find((c) => c.id === activeId) || sortedCats[0];
  const products = (active?.products || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      <section
        id="products"
        data-testid="products-section"
        className="relative py-24 md:py-32 px-6 md:px-10 bg-black/60 backdrop-blur-sm border-y border-[var(--accent-dim)]"
      >
        <div className="max-w-[100rem] mx-auto">
          <div className="mb-12 md:mb-16 grid md:grid-cols-[1fr_auto] gap-8 items-end">
            <div>
              <div className="bracket mb-4 flex items-center gap-3">
                <span className="num-solid text-[10px]">/02</span>
                STOREFRONT
              </div>
              <h2
                className="font-display text-5xl md:text-6xl lg:text-7xl font-extrabold uppercase tracking-[-0.03em] leading-[0.9]"
              >
                THE<br />
                <span className="text-[var(--accent)] text-glow">SHOP.</span>
              </h2>
              <p className="mt-6 text-[var(--muted)] max-w-xl text-base md:text-lg leading-relaxed">
                Add items to your cart and check out in one go. CFX login required — assets ship encrypted to your CFX account via Tebex.
              </p>
            </div>
            <div className="hidden md:block num-solid text-[140px] lg:text-[180px] opacity-20">
              ×{sortedCats.length}
            </div>
          </div>

          {/* Category tab bar */}
          <div
            data-testid="products-tabs"
            className="flex flex-wrap gap-2.5 mb-10"
          >
            {sortedCats.map((c) => {
              const isActive = c.id === activeId;
              return (
                <button
                  key={c.id}
                  data-testid={`products-tab-${c.id}`}
                  onClick={() => setActiveId(c.id)}
                  className={`nm-tab ${isActive ? "is-active" : ""} px-6 py-3 font-bold text-xs tracking-[0.16em] uppercase flex items-center justify-center gap-2`}
                >
                  <ShoppingBag size={12} />
                  {c.name}
                  <span className="ml-1 font-mono text-[10px] opacity-60">
                    {c.products?.length || 0}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Active category description */}
          {active?.description && (
            <p className="font-mono text-xs text-[var(--muted)] mb-6 uppercase tracking-widest">
              // {active.description}
            </p>
          )}

          {/* Products grid OR Coming Soon */}
          {products.length === 0 ? (
            <ComingSoonLED name={active?.name || "this section"} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {products.map((p, i) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  index={i}
                  onOpen={() => setOpenProduct(p)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* PRODUCT MODAL */}
      {openProduct && lightboxIdx === null && (
        <div
          data-testid="product-modal"
          className="fixed left-0 right-0 top-16 bottom-0 z-[100] bg-black/95 backdrop-blur-md overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setOpenProduct(null); }}
        >
          <div className="min-h-full p-6 md:p-12 max-w-[100rem] mx-auto">
            <div className="flex items-start justify-between gap-6 pb-6 mb-8 border-b border-[var(--accent-dim)] sticky top-0 bg-black/90 backdrop-blur-md z-10 pt-2">
              <div>
                <div className="bracket mb-2">// PRODUCT_DETAIL</div>
                <h3
                  data-text={openProduct.title}
                  className="glitch font-display text-3xl md:text-5xl font-extrabold uppercase tracking-[-0.02em]"
                >
                  {openProduct.title}
                </h3>
                {openProduct.price && (
                  (() => {
                    const dp = applyDiscount(openProduct.price, openProduct.discount);
                    if (dp) {
                      return (
                        <div className="mt-3 flex flex-col gap-1">
                          <span className="font-mono text-base sale-strike leading-none w-fit">
                            {openProduct.price}
                          </span>
                          <div className="flex items-end gap-3">
                            <span className="font-display text-4xl md:text-5xl text-[var(--accent)] sale-glow leading-none tracking-wide">
                              {dp}
                            </span>
                            <span className="bg-red-500 text-white font-mono text-[10px] font-bold tracking-widest px-2 py-1 sale-glow animate-pulse">
                              {openProduct.sale_label || `-${openProduct.discount}% SALE`}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div className="mt-3 font-display text-4xl md:text-5xl text-[var(--accent)] tracking-wide">
                        {openProduct.price}
                      </div>
                    );
                  })()
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  data-testid="product-share"
                  type="button"
                  onClick={handleCopyShareLink}
                  title="Copy share link — Discord/Twitter will preview with this product's thumbnail"
                  className="group inline-flex items-center gap-2 h-11 px-3 md:px-4 rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition font-mono text-[10px] md:text-xs uppercase tracking-widest"
                >
                  <Link2 size={14} />
                  <span className="hidden md:inline">Share</span>
                </button>
                <button
                  data-testid="product-modal-close"
                  onClick={() => setOpenProduct(null)}
                  className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-[1.4fr_1fr] gap-8">
              <div>
                {openProduct.images?.length ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {openProduct.images.map((img, idx) => (
                      <button
                        key={img.id}
                        onClick={() => setLightboxIdx(idx)}
                        className="aspect-[4/3] border border-[var(--theme-border)] hover:border-[var(--accent)] rounded-xl overflow-hidden group"
                      >
                        <Media
                          url={img.url}
                          alt={img.caption || openProduct.title}
                          mode="thumb"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="aspect-[4/3] border border-[var(--line-strong)] flex items-center justify-center font-mono text-xs text-[var(--muted)]">
                    // no images
                  </div>
                )}
              </div>

              <div className="space-y-6 md:sticky md:top-24 md:self-start">
                {openProduct.description && (
                  <p className="text-[var(--text)] leading-relaxed border-l-2 border-[var(--accent)] pl-4 whitespace-pre-line">
                    {openProduct.description}
                  </p>
                )}
                <AddToCartButton product={openProduct} large />
                <div className="font-mono text-[10px] text-[var(--muted)] tracking-widest uppercase">
                  // payment + delivery handled by tebex
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {openProduct && lightboxIdx !== null && openProduct.images?.[lightboxIdx] && (
        <div
          data-testid="product-lightbox"
          className="fixed inset-0 z-[110] bg-black/98 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLightboxIdx(null); }}
        >
          <button
            onClick={() => setLightboxIdx(null)}
            className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
          >
            <X size={18} />
          </button>
          {openProduct.images.length > 1 && (
            <>
              <button
                onClick={() => setLightboxIdx((i) => (i - 1 + openProduct.images.length) % openProduct.images.length)}
                className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
              ><ChevronLeft size={20} /></button>
              <button
                onClick={() => setLightboxIdx((i) => (i + 1) % openProduct.images.length)}
                className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-black transition z-10"
              ><ChevronRight size={20} /></button>
            </>
          )}
          <Media
            url={openProduct.images[lightboxIdx].url}
            alt={openProduct.images[lightboxIdx].caption || openProduct.title}
            mode="lightbox"
            className="max-w-[90vw] max-h-[88vh] object-contain border border-[var(--accent-dim)]"
          />
        </div>
      )}
    </>
  );
}

function ProductCard({ product, index, onOpen }) {
  const thumb = product.images?.[0]?.url || "";
  const discountedPrice = applyDiscount(product.price, product.discount);
  const onSale = !!discountedPrice;
  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="hover-rise group relative bg-[#0a0a0a] border border-[var(--line-strong)] flex flex-col overflow-hidden slash-tl"
    >
      <button
        onClick={onOpen}
        className="relative aspect-[4/3] bg-black overflow-hidden text-left"
      >
        {thumb ? (
          <>
            <Media
              url={thumb}
              alt={product.title}
              mode="thumb"
              className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
            />
            {isVideoUrl(thumb) && (
              <span className="absolute bottom-3 left-3 bg-black/80 border border-[var(--accent)]/60 text-[var(--accent)] font-mono text-[9px] tracking-widest px-2 py-1 flex items-center gap-1">
                <Play size={9} fill="currentColor" /> VIDEO
              </span>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center font-mono text-xs text-[var(--muted)]">
            // no image
          </div>
        )}
        {product.badge && (
          <span className="absolute top-3 left-3 bg-[var(--accent)] text-black font-mono text-[10px] font-bold tracking-widest px-2 py-1">
            {product.badge}
          </span>
        )}
        {onSale && (
          <span className="absolute top-3 left-3 bg-red-500 text-white font-mono text-[10px] font-bold tracking-widest px-2 py-1 sale-glow animate-pulse"
                style={{ marginTop: product.badge ? 28 : 0 }}>
            {product.sale_label || `-${product.discount}% SALE`}
          </span>
        )}
        <div className="absolute top-3 right-3 font-mono text-[10px] text-[var(--accent)] tracking-widest">
          /{String(index + 1).padStart(2, "0")}
        </div>
        <div className="absolute inset-x-0 bottom-0 h-1 bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent opacity-0 group-hover:opacity-100 transition" />
      </button>

      <div className="p-5 flex flex-col gap-3 flex-grow">
        <button onClick={onOpen} className="text-left">
          <h3 className="font-display text-xl md:text-2xl font-extrabold uppercase tracking-[-0.02em] leading-tight hover:text-[var(--accent)] transition">
            {product.title}
          </h3>
          {product.description && (
            <p className="text-xs text-[var(--muted)] mt-2 line-clamp-2">
              {product.description}
            </p>
          )}
        </button>

        <div className="mt-auto flex items-end justify-between gap-3 pt-3 border-t border-[var(--line)]">
          <div className="min-w-0">
            {product.price ? (
              onSale ? (
                <div className="flex flex-col gap-0.5">
                  <span className="font-mono text-xs sale-strike leading-none">
                    {product.price}
                  </span>
                  <span className="font-display text-3xl text-[var(--accent)] sale-glow leading-none tracking-wide">
                    {discountedPrice}
                  </span>
                </div>
              ) : (
                <div className="font-display text-3xl text-[var(--accent)] leading-none tracking-wide">
                  {product.price}
                </div>
              )
            ) : (
              <div className="font-mono text-[10px] text-[var(--muted)] uppercase tracking-widest">
                see details
              </div>
            )}
          </div>
          <AddToCartButton product={product} small />
        </div>
      </div>
    </div>
  );
}

function AddToCartButton({ product, large, small }) {
  const { add, has } = useCart();
  const inCart = has(product.id);
  const canCart = !!(product.tebex_package_id && String(product.tebex_package_id).trim());
  const canAcquire = !!product.purchase_url;
  const size = large ? "!py-4 !px-7 !text-sm" : small ? "!py-2 !px-3 !text-[11px]" : "";

  // Prefer Cart flow if Tebex package_id is set; otherwise fall back to direct Tebex link
  if (canCart) {
    return (
      <button
        type="button"
        data-testid={`add-to-cart-${product.id}`}
        onClick={(e) => {
          e.stopPropagation();
          add(product);
        }}
        className={`btn-mega ${size} ${inCart ? "!bg-black !text-[var(--accent)] !border-[var(--accent)]" : ""}`}
      >
        {inCart ? (
          <>
            <Check size={12} /> IN CART
          </>
        ) : (
          <>
            <ShoppingCart size={12} /> ADD TO CART
          </>
        )}
      </button>
    );
  }

  if (canAcquire) {
    return (
      <a
        data-testid={`acquire-btn-${product.id}`}
        href={product.purchase_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`btn-mega ${size}`}
      >
        <ExternalLink size={12} /> ACQUIRE
      </a>
    );
  }

  return (
    <span
      data-testid={`acquire-disabled-${product.id}`}
      className={`btn-mega ${size} opacity-40 cursor-not-allowed`}
      aria-disabled="true"
    >
      // LINK PENDING
    </span>
  );
}

function AcquireButton({ url, large, small }) {
  const disabled = !url;
  const size = large ? "!py-4 !px-7 !text-sm" : small ? "!py-2 !px-3 !text-[11px]" : "";
  return (
    <a
      data-testid="acquire-btn"
      href={disabled ? undefined : url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => disabled && e.preventDefault()}
      aria-disabled={disabled}
      className={`btn-mega ${size} ${disabled ? "opacity-40 cursor-not-allowed hover:!shadow-none hover:!translate-x-0 hover:!translate-y-0 hover:!bg-[var(--accent)] hover:!text-black" : ""}`}
    >
      {disabled ? "// LINK PENDING" : (<><ExternalLink size={12} /> ACQUIRE</>)}
    </a>
  );
}
